import { FunctionComponent, useEffect, useState } from 'react'
import styled from 'styled-components'
import HeaderNav from './HeaderNav'
import HeaderSearch from './HeaderSearch'
import useWindowWidth from '../../hooks/useWindowWidth'
import AppLogo from '../AppLogo/AppLogo'

import { isAuthenticated } from '../../utils/auth/auth'
import Login from './Login'
import Logout from './Logout'
import FocusLock from '../FocusLock/FocusLock'
import { Breakpoint } from '../../hooks/useBreakpoint'
import Hidden from '../Theme/Hidden'

const StyledMenuContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: flex-end;
`

const UserFocusLock = styled(FocusLock)`
  position: relative;
`

interface HeaderMenuProps {
  displayTitle: boolean
}

const HeaderMenu: FunctionComponent<HeaderMenuProps> = ({ displayTitle }) => {
  const [activeMenu, setActiveMenu] = useState<null | string>(null)
  const width = useWindowWidth()

  // Prevent the main app body scrolling in the background
  // Be sure to include document.body to prevent annoying scroll jumps on iOS keyboard open/close
  useEffect(() => {
    if (activeMenu === 'navigation') {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = 'visible'
      document.body.style.overflow = 'visible'
    }
  }, [activeMenu])

  // Fix any scroll changes if the user resizes the window while the menu is open
  useEffect(() => {
    if (!activeMenu) {
      return
    }

    // 768px is laptopM
    if (width > 1200) {
      document.documentElement.style.overflow = 'visible'
      document.body.style.overflow = 'visible'
    } else {
      // If the user resized from mobile -> desktop with the menu active and then scrolls down before
      // resizing back to mobile, we need to reset the scroll otherwise the menu displays without the
      // primary header so the user cannot close the menu
      document.body.scroll(0, 0)

      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    }
  }, [width])

  const isMobile = width < Breakpoint.Md.valueOf()
  // if on mobile and active menu is not search, show logo
  // if on desktop always show logo
  return (
    <>
      {(activeMenu !== 'search' || !isMobile) && (
        <AppLogo displayTitle={displayTitle} width={width} />
      )}

      <StyledMenuContainer data-testid="header-menu-default">
        {!displayTitle && (
          <HeaderSearch
            isExpanded={activeMenu === 'search'}
            toggleMenu={() => setActiveMenu(activeMenu !== 'search' ? 'search' : null)}
            isMobile={isMobile}
          />
        )}

        <Hidden maxBreakpoint={Breakpoint.Md}>
          {!isAuthenticated() ? (
            <Login />
          ) : (
            <UserFocusLock isLocked={activeMenu === 'user'}>
              <Logout
                isExpanded={activeMenu === 'user'}
                toggleMenu={() => {
                  setActiveMenu(activeMenu !== 'user' ? 'user' : null)
                }}
              />
            </UserFocusLock>
          )}
        </Hidden>

        <FocusLock isLocked={activeMenu === 'navigation'}>
          <>
            {isMobile && activeMenu !== 'search' && (
              <HeaderNav
                isMobile
                isExpanded={activeMenu === 'navigation'}
                toggleMenu={() => {
                  setActiveMenu(activeMenu !== 'navigation' ? 'navigation' : null)
                }}
              />
            )}

            {!isMobile && (
              <HeaderNav
                isExpanded={activeMenu === 'navigation'}
                toggleMenu={() => {
                  setActiveMenu(activeMenu !== 'navigation' ? 'navigation' : null)
                }}
              />
            )}
          </>
        </FocusLock>
      </StyledMenuContainer>
    </>
  )
}

export default HeaderMenu
